import { useEffect, useState } from "react";
import { Button, Input, Loading, showToast } from "@xpense/ui";
import { api } from "../api/client";
import { EmptyState, Page, RetryButton, Section } from "../components/Page";
import { Icon } from "../components/Icons";

export default function Settings() {
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.get<any>("/api/v1/settings");
      setSettings(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await api.patch<any>("/api/v1/settings", settings);
      setSettings(updated);
      showToast("Settings saved", "success");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to save settings", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Page
        eyebrow="Workspace"
        title="Settings"
        description="Tune locale, AI review confidence, and product behavior."
      >
        <Section>
          <div className="xpn-section-body">
            <Loading variant="text" count={6} />
          </div>
        </Section>
      </Page>
    );
  }

  if (error) {
    return (
      <Page eyebrow="Workspace" title="Settings">
        <Section>
          <EmptyState
            icon="warning"
            title="Unable to load settings"
            description={error}
            action={<RetryButton onClick={loadSettings} />}
            tone="danger"
          />
        </Section>
      </Page>
    );
  }

  if (!settings) {
    return (
      <Page eyebrow="Workspace" title="Settings">
        <Section>
          <EmptyState
            icon="settings"
            title="No settings found"
            description="Settings could not be loaded. Please try again."
            action={<RetryButton onClick={loadSettings} />}
          />
        </Section>
      </Page>
    );
  }

  const threshold = Number(settings.confidence_threshold ?? 0.7);

  return (
    <Page
      eyebrow="Workspace"
      title="Settings"
      description="Keep the experience aligned with Indian locale defaults and your review tolerance."
      action={
        <Button onClick={handleSave} loading={saving} variant="primary">
          <Icon name="check" />
          Save Settings
        </Button>
      }
    >
      <div className="xpn-grid xpn-grid--2">
        <Section title="Locale" description="Defaults used across summaries, exports, and formatting.">
          <div className="xpn-section-body xpn-flex-col xpn-gap-lg">
            <Input
              label="Currency"
              value={settings.currency || ""}
              onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
              placeholder="INR"
            />

            <Input
              label="Locale"
              value={settings.locale || ""}
              onChange={(e) => setSettings({ ...settings, locale: e.target.value })}
              placeholder="en-IN"
            />
          </div>
        </Section>

        <Section
          title="AI review gate"
          description="Transactions below this confidence score move to review."
        >
          <div className="xpn-section-body">
            <div className="xpn-flex-between xpn-mb-md">
              <span className="xpn-text-body-md xpn-text" style={{ fontWeight: 650 }}>
                Confidence threshold
              </span>
              <span data-tnum className="xpn-text-body-tabular xpn-text">
                {(threshold * 100).toFixed(0)}%
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={threshold}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  confidence_threshold: parseFloat(e.target.value),
                })
              }
              className="xpn-range"
            />
            <p className="xpn-text-caption xpn-text-muted xpn-mt-md">
              Higher values increase manual review but protect data quality.
            </p>
          </div>
        </Section>
      </div>

      <Section title="Automation" description="Control what the product can process without asking.">
        <div className="xpn-section-body">
          <label className="xpn-toggle-row">
            <div>
              <div className="xpn-text-body-md xpn-text" style={{ fontWeight: 650 }}>
                Enable notifications
              </div>
              <div className="xpn-text-caption xpn-text-muted">
                Allow product notifications for review and processing updates.
              </div>
            </div>
            <input
              type="checkbox"
              checked={Boolean(settings.notifications_enabled)}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  notifications_enabled: e.target.checked,
                })
              }
              className="xpn-toggle"
            />
          </label>

          <label className="xpn-toggle-row">
            <div>
              <div className="xpn-text-body-md xpn-text" style={{ fontWeight: 650 }}>
                Auto-classify transactions
              </div>
              <div className="xpn-text-caption xpn-text-muted">
                Queue new raw events for AI classification automatically.
              </div>
            </div>
            <input
              type="checkbox"
              checked={Boolean(settings.auto_classify)}
              onChange={(e) => setSettings({ ...settings, auto_classify: e.target.checked })}
              className="xpn-toggle"
            />
          </label>
        </div>
      </Section>
    </Page>
  );
}
